/**
 * ExecuteTooltipContent — detailed, reusable tooltip body for "Execute" buttons.
 *
 * Explains exactly how the auto-computed order quantity is derived: the full
 * position size, how it is split into legs per the user's Entry Split Count
 * setting, what share of the Investment Amount a single click uses, and the
 * Portfolio Reserve gate. Shared by the Dashboard Recent/Parked alert rows.
 */
import React from 'react';
import { Box } from '@mui/material';

interface Props {
  action?: string | null;
  /** user_settings.entrySplitCount (1-5; defaults to 3 when unknown). */
  entrySplitCount?: number | null;
  /** user_settings.portfolioReservePercent. */
  portfolioReservePercent?: number | null;
}

const ExecuteTooltipContent: React.FC<Props> = ({ action, entrySplitCount, portfolioReservePercent }) => {
  const splits = Math.max(1, Math.min(5, Number(entrySplitCount) || 3));
  // Doubling rule: first two legs equal, each subsequent doubles, last = half.
  // So the FIRST leg (this click) is 1 / 2^(N-1) of the total (N=1 → whole).
  const firstLegPct = splits === 1 ? 100 : +(100 / Math.pow(2, splits - 1)).toFixed(2);
  const splitMap: Record<number, string> = {
    1: 'whole', 2: '½, ½', 3: '¼, ¼, ½', 4: '⅛, ⅛, ¼, ½', 5: '1/16, 1/16, ⅛, ¼, ½',
  };
  const act = (action || 'alert').toString().toUpperCase();
  return (
    <Box sx={{ fontSize: '0.78rem', lineHeight: 1.5, maxWidth: 330 }}>
      <Box sx={{ fontWeight: 700, mb: 0.5 }}>Execute — {act}</Box>
      Opens the order panel pre-filled so you can review entry / SL / target before placing a live order.

      <Box sx={{ mt: 0.75, fontWeight: 700 }}>Auto-computed quantity</Box>
      Full position qty = Investment Amount × Leverage ÷ (entry price × lot size), using this symbol's
      values from the Instruments page.

      <Box sx={{ mt: 0.75 }}>
        That total is split into <b>{splits}</b> {splits === 1 ? 'leg' : 'legs'} per your{' '}
        <b>Entry Split Count = {splits}</b> (Settings → Trading) using a <b>doubling rule</b>
        {splits > 1 && <> ({splitMap[splits]} of the total — first two legs equal, each next doubles, last leg absorbs rounding)</>}.
        This click places the <b>first leg ≈ {firstLegPct}%</b> of the full position.
        {splits > 1 && <> Click Execute again to add the next (larger) leg, up to the {splits}-split cap.</>}
      </Box>

      <Box sx={{ mt: 0.75 }}>
        <b>Portfolio Reserve{portfolioReservePercent != null ? ` ${portfolioReservePercent}%` : ''}</b> still
        gates: if free margin is below the threshold the order is rejected. Applies whether Auto-Trade is on or off.
      </Box>
    </Box>
  );
};

export default ExecuteTooltipContent;
